// BlockKbPage — generic renderer for any page_content row at slug `kb-doc/...`
// whose content is the BlockRenderer recipe shape ({ blocks: [...] }).
//
// Wired to the catch-all `/kb-doc/*` route in CRM.tsx. The slug is derived
// from the wildcard path tail via useParams (params["*"]).
//
// MJOS-104 / DEM-21: if the slug doesn't match a page_content row, the API
// falls back to page_redirects and returns { redirect_to: "<new-slug>" }.
// We follow it client-side via React Router replace() so previously-shared
// URLs (and the legacy URL-form slugs inserted by DEM-24) keep working.
//
// DEM-40 follow-up: top-right DownloadMenu (PDF + Markdown) ported from the
// erez tenant (MJOS-101). PDF is screenshot-based via html-to-image + jsPDF
// (matches what's on screen); Markdown is a clean per-block serialization of
// the recipe in lib/download-md.ts.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { architectureConfig } from "@/components/blocks/architectureConfig";
import { BlockRenderer, type Block } from "@/components/blocks/BlockRenderer";
import { useApi } from "@/lib/api";
import { DownloadMenu } from "./DownloadMenu";

type KbResponse = {
  content?: { blocks?: Block[] } | null;
  redirect_to?: string;
  nav_label?: string | null;
};

export function BlockKbPage() {
  const api = useApi();
  const navigate = useNavigate();
  const params = useParams();
  const tail = params["*"] ?? "";
  const slug = tail ? `kb-doc/${tail}` : "";

  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setBlocks(null);
    setError(null);
    (async () => {
      try {
        const res = await api(`/api/kb/${slug}`);
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          return;
        }
        const body = (await res.json()) as KbResponse;
        if (cancelled) return;

        if (body.redirect_to) {
          navigate(`/${body.redirect_to}`, { replace: true });
          return;
        }

        if (Array.isArray(body.content?.blocks)) {
          setBlocks(body.content?.blocks ?? null);
        } else {
          setError(
            `Page content at slug ${slug} is not a BlockRenderer recipe. Expected { blocks: [...] }.`,
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // api + navigate are stable enough; depending on them re-fires this effect
    // every render (useApi returns a new function identity per call) which
    // would hang the page in a re-fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!blocks && !error) {
    return (
      <PageFrame>
        <div className="flex items-center gap-2 p-2 text-sm text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          טוען…
        </div>
      </PageFrame>
    );
  }

  if (error) {
    return (
      <PageFrame>
        <div className="rounded-2xl border border-rose-300/60 bg-rose-50/40 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <div className="text-sm font-semibold text-rose-700">
            לא ניתן לטעון את העמוד
          </div>
          <div className="mt-1 text-xs text-stone-500">
            Slug:{" "}
            <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono">
              {slug}
            </code>
          </div>
          <div className="mt-2 text-xs text-rose-600/80">{error}</div>
        </div>
      </PageFrame>
    );
  }

  // Last path segment, used as the download filename stem.
  const filename = tail.split("/").pop() || "knowledge-base";

  return (
    <PageFrame>
      <div className="mb-4 flex justify-end">
        <DownloadMenu pageRef={contentRef} blocks={blocks!} filename={filename} />
      </div>
      <div ref={contentRef}>
        <BlockRenderer config={architectureConfig} blocks={blocks!} />
      </div>
    </PageFrame>
  );
}

// DEM-36 follow-up: Layout owns the page chrome (sky-blue bg via
// SKY_BLUE_ROUTES, max-w-[1000px] via NARROW_ROUTES, padding). PageFrame
// here is just the RTL wrapper so Hebrew KB pages render right-to-left.
function PageFrame({ children }: { children: React.ReactNode }) {
  return <div dir="rtl">{children}</div>;
}
